/****
 * Alpine.js directive that makes a container element behave like a clickable element (link or button).
 * - If the container has exactly one interactive child (<a> or <button>), clicks on the container are proxied to that child.
 * - If there are no interactive children, the directive handles fallback behavior:
 *    - If `x-clickable:href` is present, it acts as a link and navigates to the specified href.
 *    - If `x-clickable` is used with an expression, that expression will act as the fallback click handler when no interactive child is present.
 *    - Any `x-clickable:*` attributes will be applied to the container element once the directive is enabled.
 * - If the clickable condition is not met, all `x-clickable:*` attributes will be ignored.
 * - The directive manages ARIA roles and tabindex to ensure accessibility.
 *
 * Usage:
 *   <!-- Proxy clicks to a single child <a> -->
 *   <div x-clickable x-clickable:class="is-link">
 *     <img src="https://example.com/sample.jpg" />
 *     <div class="rich-text">
 *       <a href="https://example.com">Link</a>
 *     </div>
 *   </div>
 *
 *   <!-- Proxy clicks to a single child <button> -->
 *   <div x-clickable x-clickable:class="is-button">
 *     <img src="https://example.com/sample.jpg" />
 *     <div class="rich-text">
 *       <button @click="doSomething">Button</button>
 *     </div>
 *   </div>
 * 
 *   <!-- Fallback when the container has no link or button children -->
 * 
 *   <div x-clickable:href="https://example.com" x-clickable:target="_blank">
 *     <img src="https://example.com/sample.jpg" />
 *     <div class="rich-text">
 *       <p>No link/button rich-text content</p>
 *     </div>
 *   </div>
 * 
 *   <div x-clickable="alert('clicked me')" x-clickable:class="is-button">
 *     <img src="https://example.com/sample.jpg" />
 *     <div class="rich-text">
 *       <p>No link/button rich-text content</p>
 *     </div>
 *   </div>
 */
export default function clickable(Alpine) {
    Alpine.directive("clickable", (el, { value: attr, expression }, { evaluate }) => {
        // Initialize the _x_clickable property if it doesn't exist; stores state, attributes, and dispatch logic
        if (!el._x_clickable) {
            el._x_clickable = Alpine.reactive({
                enable: false,
                target: null,
                attributes: { role: "", tabindex: "0" },
                dispatch(event) {
                    if (!this.enable) return;

                    if (this.target) {
                        if (event.target === this.target || closestNode(event.target, this.target)) return;
                        this.target.click();
                    } else if (el.getAttribute("x-clickable")) {
                        try{
                            evaluate(el.getAttribute("x-clickable"));
                        } catch(e) {
                            console.warn("Unable to execute 'x-clickable'", el.getAttribute("x-clickable"));
                        }
                    } else if (el.getAttribute("x-clickable:href")) {
                        const a = document.createElement("a");
                        a.href = el.getAttribute("x-clickable:href");
                        a.target = el.getAttribute("x-clickable:target") || "_self";
                        document.body.appendChild(a);
                        a.click();
                        a.remove();
                    }
                }
            });

            // Single click listener
            el.addEventListener("click", (e) => el._x_clickable.dispatch(e));
        }

        // Reactive effect: automatically recalculates when value/expression or child elements change
        Alpine.effect(() => {
            // Determine if the element has interactive children and gather information about them
            const { hasInteractiveChildren, firstInteractiveChild, interactiveChildrenCount } = getActions(el);

            // If no target element is set and there is exactly one interactive child, set it as the target and enable the directive
            if (el._x_clickable.target === null && hasInteractiveChildren) {
                if (interactiveChildrenCount === 1) {
                    const role = firstInteractiveChild.tagName.toLowerCase() === "a" ? "link" : "button";
                    el._x_clickable.target = firstInteractiveChild;
                    el._x_clickable.enable = true;
                    el._x_clickable.attributes["role"] = role;
                } else {
                    // Ignore if multiple interactive children are found — x-clickable only supports a single link or button child
                    el._x_clickable.enable = false;
                }
            }

            // Handle attributes and expressions passed to the directive
            if (attr) {
                if (attr === "href") {
                    // If no interactive children, set role to link and enable the directive
                    if (!hasInteractiveChildren) {
                        el._x_clickable.attributes["role"] = "link";
                        el._x_clickable.enable = true;
                    }
                } else {
                    // Apply other directive attributes directly
                    el._x_clickable.attributes[attr] = expression;
                }
            } else {
                // If expression is provided and no interactive children, set role to button and enable
                if (expression && !hasInteractiveChildren) {
                    el._x_clickable.attributes["role"] = "button";
                    el._x_clickable.enable = true;
                }
            };
                
            // If directive is enabled, apply attributes to the container element and its single interactive child if present
            if (el._x_clickable.enable) {
                // Apply attributes reactively
                setAttributes(el, el._x_clickable.attributes);
                if (el._x_clickable.target) {
                    setAttributes(el._x_clickable.target, { role: "none", tabindex: "-1" });
                }
            }
        });
    })
}

/**
 * Traverses up the DOM tree from the given element to find the closest ancestor that matches the target node.
 *
 * @param {Element} el - The starting element from which to begin the search.
 * @param {Element} target - The target ancestor element to match.
 * @returns {Element|null} - Returns the target node if found in the ancestor chain, otherwise null.
 */
function closestNode(el, target) {
  let node = el
  while (node) {
    if (node === target) return node
    node = node.parentElement
  }
  return null
}

/**
 * Retrieves information about interactive children (anchor and button elements) within the given element.
 *
 * @param {Element} element - The DOM element to search within.
 * @returns {Object} An object containing:
 *  - hasInteractiveChildren {boolean}: True if there are any <a> or <button> children.
 *  - firstInteractiveChild {Element|null}: The first interactive child if exactly one exists, otherwise null.
 *  - interactiveChildrenCount {number}: The count of interactive children found.
 */
function getActions(element) {
    const actions = element.querySelectorAll("a, button");
    return {
        hasInteractiveChildren: actions.length > 0,
        firstInteractiveChild: actions.length === 1 ? actions[0] : null,
        interactiveChildrenCount: actions.length,
    };
}

/**
 * Sets multiple attributes on a given element.
 * For the "class" attribute, classes are added individually to avoid overwriting existing classes.
 * Other attributes are only set if they do not already exist on the element.
 *
 * @param {Element} element - The DOM element to set attributes on.
 * @param {Object} attributes - An object where keys are attribute names and values are attribute values.
 */
function setAttributes (element, attributes) {
    for (const attribute in attributes) {
        const attributeValue = attributes[attribute];
        if (attribute == "class") {
            // Add classes individually to avoid overwriting existing classes
            element.classList.add(...attributeValue.split(" "));
        } else {
            // Only set the attribute if it doesn’t already exist on the element
            if(!element.hasAttribute(attribute)) element.setAttribute(attribute, attributeValue);
        }
    }            
}